import { useState, useMemo, useEffect } from 'react';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Mail, Phone, MapPin, Globe, Pencil, Camera, Check, X, AlertCircle, ChevronDown, ChevronUp, Briefcase, GraduationCap, Clock, Flag, type LucideIcon } from 'lucide-react';
import { AvatarEditorDialog } from '@/components/seek/AvatarEditorDialog';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useToast } from '@/hooks/use-toast';
import { COUNTRY_CITIES, COUNTRIES } from '@/data/locationData';
import { useTranslation } from 'react-i18next';
import { NATIONALITY_AR, COUNTRY_AR, CITY_AR, EDUCATION_LEVEL_AR } from '@/data/arabicLookups';
import { cn } from '@/lib/utils';
import { detectTextDirection } from '@/lib/textDirection';
import { RESPONSIVE_METRIC_CONTENT, SAFE_COMPACT_VALUE, SAFE_TEXT_WRAP } from '@/lib/responsiveText';
import type { Tables } from '@/integrations/supabase/types';

const NATIONALITIES = [
  'Afghan', 'Albanian', 'Algerian', 'American', 'Argentinian', 'Australian',
  'Bahraini', 'Bangladeshi', 'Belgian', 'Brazilian', 'British', 'Bulgarian',
  'Canadian', 'Chinese', 'Colombian', 'Croatian', 'Czech', 'Danish', 'Dutch',
  'Egyptian', 'Emirati', 'Ethiopian', 'Filipino', 'Finnish', 'French',
  'German', 'Greek', 'Hungarian', 'Indian', 'Indonesian', 'Iranian', 'Iraqi',
  'Irish', 'Italian', 'Japanese', 'Jordanian', 'Kazakh', 'Kenyan', 'Korean',
  'Kuwaiti', 'Lebanese', 'Libyan', 'Malaysian', 'Mexican', 'Moroccan',
  'Nepali', 'New Zealander', 'Nigerian', 'Norwegian', 'Omani', 'Pakistani',
  'Palestinian', 'Peruvian', 'Polish', 'Portuguese', 'Qatari', 'Romanian',
  'Russian', 'Saudi', 'Senegalese', 'Serbian', 'Singaporean', 'Slovak',
  'South African', 'Spanish', 'Sri Lankan', 'Sudanese', 'Swedish', 'Swiss',
  'Syrian', 'Taiwanese', 'Thai', 'Tunisian', 'Turkish', 'Ukrainian',
  'Venezuelan', 'Vietnamese', 'Yemeni',
].sort();

const EDUCATION_LEVELS = [
  'High School', 'Diploma', "Associate's Degree", "Bachelor's Degree",
  "Master's Degree", 'MBA', 'Doctorate / PhD', 'Professional Certification',
];

type PersonalDetailsProfile = Pick<
  Tables<'candidate_profiles'>,
  | 'avatar_url'
  | 'availability_status'
  | 'city'
  | 'country'
  | 'current_job_title'
  | 'headline'
  | 'highest_education_level'
  | 'nationality'
  | 'notice_period'
  | 'phone'
  | 'professional_summary'
  | 'total_years_experience'
>;

interface Props {
  profile: PersonalDetailsProfile | null | undefined;
  onSave: (data: Record<string, unknown>) => void;
  saving: boolean;
  completionScore?: number;
  missingItems?: { label: string; sectionId: string; editId?: string }[];
  editLocked?: boolean;
  onEditingChange?: (active: boolean) => void;
}

export function PersonalDetailsSection({ profile, onSave, saving, completionScore = 0, missingItems = [], editLocked, onEditingChange }: Props) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const { user, profile: authProfile } = useSeekAuth();
  const { toast } = useToast();
  const [showMissing, setShowMissing] = useState(false);
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sectionId === 'section-personal') {
        if (detail?.editId === 'avatar') {
          setAvatarEditorOpen(true);
        } else if (!editLocked) {
          setEditing(true);
          onEditingChange?.(true);
        }
      }
    };
    window.addEventListener('open-section-edit', handler);
    return () => window.removeEventListener('open-section-edit', handler);
  }, []);

  const [form, setForm] = useState({
    headline: profile?.headline ?? '',
    professional_summary: profile?.professional_summary ?? '',
    current_job_title: profile?.current_job_title ?? '',
    country: profile?.country ?? '',
    city: profile?.city ?? '',
    nationality: profile?.nationality ?? '',
    total_years_experience: profile?.total_years_experience?.toString() ?? '',
    highest_education_level: profile?.highest_education_level ?? '',
    availability_status: profile?.availability_status ?? 'open',
    notice_period: profile?.notice_period ?? '',
  });

  const cityOptions = useMemo(() => {
    if (!form.country) return [];
    const cities = COUNTRY_CITIES[form.country] ?? [];
    return cities.sort().map((c) => ({ label: isAr ? (CITY_AR[c] ?? c) : c, value: c }));
  }, [form.country, isAr]);

  const countryOptions = useMemo(() => COUNTRIES.map((c) => ({ label: isAr ? (COUNTRY_AR[c] ?? c) : c, value: c })), [isAr]);
  const nationalityOptions = useMemo(() => NATIONALITIES.map((n) => ({ label: isAr ? (NATIONALITY_AR[n] ?? n) : n, value: n })), [isAr]);
  const educationOptions = useMemo(() => EDUCATION_LEVELS.map((l) => ({ label: isAr ? (EDUCATION_LEVEL_AR[l] ?? l) : l, value: l })), [isAr]);
  const availabilityOptions = useMemo(() => [
    { label: t('seek.profile.openToOpportunities'), value: 'open' },
    { label: t('seek.profile.activelyLooking'), value: 'actively_looking' },
    { label: t('seek.profile.notLooking'), value: 'not_looking' },
  ], [t]);

  const startEdit = () => {
    if (editLocked) return;
    setForm({
      headline: profile?.headline ?? '',
      professional_summary: profile?.professional_summary ?? '',
      current_job_title: profile?.current_job_title ?? '',
      country: profile?.country ?? '',
      city: profile?.city ?? '',
      nationality: profile?.nationality ?? '',
      total_years_experience: profile?.total_years_experience?.toString() ?? '',
      highest_education_level: profile?.highest_education_level ?? '',
      availability_status: profile?.availability_status ?? 'open',
      notice_period: profile?.notice_period ?? '',
    });
    setEditing(true);
    onEditingChange?.(true);
  };

  const handleSave = () => {
    const years = form.total_years_experience ? Math.max(0, Number(form.total_years_experience)) : null;
    onSave({ ...form, total_years_experience: years });
    setEditing(false);
    onEditingChange?.(false);
  };

  const handleAvatarSave = async (blob: Blob) => {
    if (!user?.id) return;
    const path = `${user.id}/avatar.png`;
    const { error: uploadErr } = await seekSupabase.storage
      .from('candidate-avatars')
      .upload(path, blob, { upsert: true, contentType: 'image/png' });
    if (uploadErr) throw uploadErr;
    const { data: urlData } = seekSupabase.storage
      .from('candidate-avatars')
      .getPublicUrl(path);
    const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    onSave({ avatar_url: avatarUrl });
    toast({ title: t('seek.profile.photoUploaded') });
  };

  const initials = authProfile?.full_name
    ?.split(' ')
    .map((w: string) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? 'U';

  const city = profile?.city;
  const country = profile?.country;
  const nationality = profile?.nationality;
  const educationLevel = profile?.highest_education_level;
  const displayCity = isAr && city ? (CITY_AR[city] ?? city) : city;
  const displayCountry = isAr && country ? (COUNTRY_AR[country] ?? country) : country;
  const location = [displayCity, displayCountry].filter(Boolean).join(', ');
  const displayNationality = isAr && nationality ? (NATIONALITY_AR[nationality] ?? nationality) : nationality;
  const displayEducation = isAr && educationLevel ? (EDUCATION_LEVEL_AR[educationLevel] ?? educationLevel) : educationLevel;
  const displayPhone = profile?.phone;

  const availabilityLabel = availabilityOptions.find(o => o.value === profile?.availability_status)?.label ?? '';

  if (!editing) {
    return (
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {/* Hero banner area */}
          <div className="relative bg-seek/5 px-6 sm:px-8 pt-6 sm:pt-8 pb-16">
            <Button variant="ghost" size="sm" onClick={startEdit} disabled={editLocked} className="absolute top-4 end-4 gap-1 bg-white/80 backdrop-blur text-seek hover:bg-seek hover:text-white border border-seek/20 shadow-sm">
              <Pencil className="w-3.5 h-3.5" /> {t('common.edit')}
            </Button>
          </div>

          {/* Profile content - pulled up over banner */}
          <div className="px-6 sm:px-8 pb-6 sm:pb-8 -mt-12">
            <div className="flex flex-col sm:flex-row gap-5">
              {/* Avatar */}
              <div className="shrink-0">
                <div className="relative group">
                  <Avatar className="w-24 h-24 border-4 border-background shadow-lg rounded-xl">
                    {profile?.avatar_url ? (
              <AvatarImage src={profile.avatar_url} alt={t('seek.profile.profilePhoto')} className="object-cover" />
                    ) : null}
                    <AvatarFallback className="text-2xl bg-seek/10 text-seek rounded-xl font-bold">{initials}</AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => setAvatarEditorOpen(true)}
                    className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    <Camera className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              <AvatarEditorDialog
                open={avatarEditorOpen}
                onOpenChange={setAvatarEditorOpen}
                onSave={handleAvatarSave}
                currentAvatarUrl={profile?.avatar_url}
              />

              {/* Name & title */}
              <div className="flex-1 min-w-0 pt-1">
                <h2 className="text-xl sm:text-2xl font-bold text-foreground font-[var(--font-display)] leading-tight">
                  {authProfile?.full_name || '—'}
                </h2>
                {profile?.current_job_title && (
                  <p className="text-sm font-semibold text-seek mt-1">{profile.current_job_title}</p>
                )}
                {availabilityLabel && (
                  <Badge variant="secondary" className="mt-2 bg-seek/10 text-seek border-seek/20 text-xs font-medium">
                    {availabilityLabel}
                  </Badge>
                )}
              </div>
            </div>

            {/* Summary */}
            {profile?.professional_summary && (
              <div className="mt-5 rounded-xl bg-muted/40 border p-4" dir={detectTextDirection(profile.professional_summary)}>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {profile.professional_summary}
                </p>
              </div>
            )}

            {/* Contact & details grid */}
            <div className="mt-5 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,12rem),1fr))]">
              <InfoCard icon={Mail} label={t('seek.profile.email')} value={authProfile?.email} />
              <InfoCard icon={Phone} label={t('seek.profile.phone')} value={displayPhone} />
              <InfoCard icon={MapPin} label={t('seek.profile.location')} value={location} />
              <InfoCard icon={Flag} label={t('seek.profile.nationality')} value={displayNationality} />
              <InfoCard icon={Briefcase} label={t('seek.profile.experience')} value={profile?.total_years_experience ? `${profile.total_years_experience} ${t('seek.profile.years')}` : null} />
              <InfoCard icon={GraduationCap} label={t('seek.profile.education')} value={displayEducation} />
              <InfoCard icon={Clock} label={t('seek.profile.noticePeriod')} value={profile?.notice_period} />
            </div>

            {/* Profile Completion Bar */}
            <div className="mt-5 pt-4 border-t border-border/50">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('seek.profile.profileCompletion')}</span>
                <span className={cn('text-xs font-bold', completionScore === 100 ? 'text-seek' : completionScore > 80 ? 'text-lime-500' : completionScore > 70 ? 'text-yellow-500' : completionScore > 60 ? 'text-orange-500' : 'text-destructive')}>{completionScore}%</span>
              </div>
              <Progress value={completionScore} className={cn('h-2', completionScore === 100 ? '[&>div]:bg-seek' : completionScore > 80 ? '[&>div]:bg-lime-500' : completionScore > 70 ? '[&>div]:bg-yellow-500' : completionScore > 60 ? '[&>div]:bg-orange-500' : '[&>div]:bg-destructive')} />
              {completionScore < 100 && missingItems.length > 0 && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setShowMissing(!showMissing)}
                    className={cn('flex items-center gap-1 text-[11px] font-medium transition-colors', completionScore > 80 ? 'text-lime-500 hover:text-lime-500/80' : completionScore > 70 ? 'text-yellow-500 hover:text-yellow-500/80' : completionScore > 60 ? 'text-orange-500 hover:text-orange-500/80' : 'text-destructive hover:text-destructive/80')}
                  >
                    <AlertCircle className="w-3 h-3" />
                    {t('seek.profile.missingItems', { count: missingItems.length })}
                    {showMissing ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {showMissing && (
                    <ul className="mt-2 space-y-1 ps-1">
                      {missingItems.map((item, idx) => (
                        <li key={idx}>
                          <button
                            type="button"
                            onClick={() => {
                              const el = document.getElementById(item.sectionId);
                              if (el) {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                window.dispatchEvent(new CustomEvent('open-section-edit', { detail: { sectionId: item.sectionId, editId: item.editId } }));
                              }
                            }}
                            className="flex items-center gap-2 text-[11px] text-muted-foreground hover:text-seek transition-colors cursor-pointer w-full text-start"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-destructive/60 shrink-0" />
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">{t('seek.profile.personalDetails')}</h3>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); onEditingChange?.(false); }}><X className="w-4 h-4" /></Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-seek text-white hover:bg-seek/90">
              <Check className="w-4 h-4 me-1" /> {t('common.save')}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('seek.profile.headline')} value={form.headline} onChange={(v) => setForm(p => ({ ...p, headline: v }))} placeholder={t('seek.profile.headlinePlaceholder')} />
          <Field label={t('seek.profile.currentJobTitle')} value={form.current_job_title} onChange={(v) => setForm(p => ({ ...p, current_job_title: v }))} />
          <div className="rounded-xl border bg-muted/30 p-3 sm:col-span-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('seek.profile.accountIdentity')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('seek.profile.identityManagedFromAccount')}
            </p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.country')}</Label>
            <SearchableSelect value={form.country} onValueChange={(v) => setForm(p => ({ ...p, country: v, city: '' }))} options={countryOptions} placeholder={t('seek.profile.selectCountry')} searchPlaceholder={t('seek.profile.searchCountries')} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.city')}</Label>
            <SearchableSelect value={form.city} onValueChange={(v) => setForm(p => ({ ...p, city: v }))} options={cityOptions} placeholder={form.country ? t('seek.profile.selectCity') : t('seek.profile.selectCountryFirst')} searchPlaceholder={t('seek.profile.searchCities')} disabled={!form.country} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.nationality')}</Label>
            <SearchableSelect value={form.nationality} onValueChange={(v) => setForm(p => ({ ...p, nationality: v }))} options={nationalityOptions} placeholder={t('seek.profile.selectNationality')} searchPlaceholder={t('seek.profile.searchNationalities')} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.yearsOfExperience')}</Label>
            <Input type="number" min={0} value={form.total_years_experience} onChange={(e) => { const val = e.target.value; if (val === '' || Number(val) >= 0) setForm(p => ({ ...p, total_years_experience: val })); }} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.highestEducation')}</Label>
            <SearchableSelect value={form.highest_education_level} onValueChange={(v) => setForm(p => ({ ...p, highest_education_level: v }))} options={educationOptions} placeholder={t('seek.profile.selectEducation')} searchPlaceholder={t('common.search')} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('seek.profile.availability')}</Label>
            <SearchableSelect value={form.availability_status} onValueChange={(v) => setForm(p => ({ ...p, availability_status: v }))} options={availabilityOptions} placeholder={t('seek.profile.selectAvailability')} />
          </div>
          <Field label={t('seek.profile.noticePeriod')} value={form.notice_period} onChange={(v) => setForm(p => ({ ...p, notice_period: v }))} placeholder={t('seek.profile.noticePeriodPlaceholder')} />
        </div>
        <div className="mt-4">
          <Label className="text-xs text-muted-foreground">{t('seek.profile.professionalSummary')}</Label>
          <Textarea value={form.professional_summary} onChange={(e) => setForm(p => ({ ...p, professional_summary: e.target.value }))} rows={4} placeholder={t('seek.profile.summaryPlaceholder')} />
        </div>
      </CardContent>
    </Card>
  );
}

function InfoCard({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value?: string | null }) {
  const valueDir = detectTextDirection(value);
  return (
    <div className="rounded-xl border bg-muted/30 p-3.5 flex items-start gap-3 min-w-0">
      <div className="w-8 h-8 rounded-lg bg-seek/10 flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-seek" />
      </div>
      <div className={RESPONSIVE_METRIC_CONTENT}>
        <p className={cn('text-[10px] font-semibold text-muted-foreground uppercase leading-snug', SAFE_TEXT_WRAP)}>{label}</p>
        <p className={cn('text-sm font-medium text-foreground mt-1', SAFE_COMPACT_VALUE)} dir={valueDir} title={value || undefined}>{value || '—'}</p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type={type ?? 'text'} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
